import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Clock, TrendingUp, TrendingDown, Activity, Truck, Package, BarChart3, Filter, Download, CheckCircle, XCircle, Zap, Warehouse } from 'lucide-react';
import { SmartDropdown, FilterSection, useToast, LoadingState, EmptyState } from '../components/ui';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import api from '../services/api';

const OperationalAnalytics = () => {
  const { success, error: showError } = useToast();

  const [dateRange, setDateRange] = useState('30');

  // Fetch bottleneck analysis
  const { data: bottleneckData, isLoading: bottleneckLoading } = useQuery({
    queryKey: ['bottleneck-analysis', dateRange],
    queryFn: async () => {
      const response = await api.get(`/analytics/operational/bottleneck_analysis/?days=${dateRange}`);
      return response.data;
    }
  });

  // Fetch waiting time analysis
  const { data: waitingTimeData, isLoading: waitingTimeLoading } = useQuery({
    queryKey: ['waiting-time-analysis', dateRange],
    queryFn: async () => {
      const response = await api.get(`/analytics/operational/waiting_time_analysis/?days=${dateRange}`);
      return response.data;
    }
  });

  // Fetch throughput analysis
  const { data: throughputData, isLoading: throughputLoading } = useQuery({
    queryKey: ['throughput-analysis', dateRange],
    queryFn: async () => {
      const response = await api.get(`/analytics/operational/throughput_analysis/?days=${dateRange}`);
      return response.data;
    }
  });

  // Fetch efficiency metrics
  const { data: efficiencyData, isLoading: efficiencyLoading } = useQuery({
    queryKey: ['efficiency-metrics', dateRange],
    queryFn: async () => {
      const response = await api.get(`/analytics/operational/efficiency_metrics/?days=${dateRange}`);
      return response.data;
    }
  });

  const handleExportReport = () => {
    const report = {
      'Date Range': `${dateRange} days`,
      'Generated': new Date().toLocaleString(),
      'Bottleneck Analysis': bottleneckData,
      'Waiting Time Analysis': waitingTimeData,
      'Throughput Analysis': throughputData,
      'Efficiency Metrics': efficiencyData
    };

    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `operational-analytics-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    success('Analytics report exported successfully!');
  };

  const getStageColor = (stageName) => {
    const colors = {
      'gate_check': 'bg-blue-100 text-blue-800 border-blue-300',
      'document_verification': 'bg-purple-100 text-purple-800 border-purple-300',
      'dock_assignment': 'bg-yellow-100 text-yellow-800 border-yellow-300',
      'unloading': 'bg-green-100 text-green-800 border-green-300',
      'qc_inspection': 'bg-orange-100 text-orange-800 border-orange-300',
      'grn_creation': 'bg-indigo-100 text-indigo-800 border-indigo-300'
    };
    return colors[stageName] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const getStageLabel = (stageName) => {
    const labels = {
      'gate_check': 'Gate Check',
      'document_verification': 'Document Verification',
      'dock_assignment': 'Dock Assignment Wait',
      'unloading': 'Unloading',
      'qc_inspection': 'QC Inspection',
      'grn_creation': 'GRN Creation'
    };
    return labels[stageName] || stageName;
  };

  const isLoading = bottleneckLoading || waitingTimeLoading || throughputLoading || efficiencyLoading;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Operational Analytics</h1>
          <p className="text-gray-600 mt-2">Bottleneck analysis, waiting times, and efficiency metrics</p>
        </div>
        <div className="flex items-center space-x-3">
          <SmartDropdown
            label=""
            name="dateRange"
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            options={[
              { value: '7', label: 'Last 7 Days' },
              { value: '30', label: 'Last 30 Days' },
              { value: '60', label: 'Last 60 Days' },
              { value: '90', label: 'Last 90 Days' }
            ]}
            icon={Filter}
          />
          <button
            onClick={handleExportReport}
            disabled={isLoading}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center space-x-2 disabled:bg-gray-300"
          >
            <Download className="w-4 h-4" />
            <span>Export Report</span>
          </button>
        </div>
      </div>

      {/* Efficiency Metrics Summary */}
      {efficiencyLoading ? (
        <LoadingState message="Loading efficiency metrics..." />
      ) : efficiencyData ? (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="card bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600 font-medium">Total Trucks</p>
                <p className="text-3xl font-bold text-blue-700 mt-1">{efficiencyData.total_trucks}</p>
              </div>
              <Truck className="w-10 h-10 text-blue-500 opacity-50" />
            </div>
          </div>

          <div className="card bg-gradient-to-br from-green-50 to-green-100 border-2 border-green-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600 font-medium">Completion Rate</p>
                <p className="text-3xl font-bold text-green-700 mt-1">{efficiencyData.completion_rate_percent}%</p>
              </div>
              <CheckCircle className="w-10 h-10 text-green-500 opacity-50" />
            </div>
          </div>

          <div className="card bg-gradient-to-br from-yellow-50 to-yellow-100 border-2 border-yellow-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-yellow-600 font-medium">Avg Completion</p>
                <p className="text-3xl font-bold text-yellow-700 mt-1">{efficiencyData.avg_completion_time_hours}h</p>
              </div>
              <Clock className="w-10 h-10 text-yellow-500 opacity-50" />
            </div>
          </div>

          <div className="card bg-gradient-to-br from-purple-50 to-purple-100 border-2 border-purple-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-600 font-medium">Efficiency Score</p>
                <p className="text-3xl font-bold text-purple-700 mt-1">{efficiencyData.efficiency_score}</p>
              </div>
              <Zap className="w-10 h-10 text-purple-500 opacity-50" />
            </div>
          </div>
        </div>
      ) : null}

      {/* Bottleneck Analysis */}
      {bottleneckLoading ? (
        <LoadingState message="Analyzing bottlenecks..." />
      ) : bottleneckData ? (
        <div className="card">
          <div className="flex items-center space-x-2 mb-4">
            <AlertTriangle className="w-6 h-6 text-red-600" />
            <h2 className="text-2xl font-bold">Bottleneck Analysis</h2>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-blue-800">
              <strong>Analysis Period:</strong> Last {bottleneckData.period_days} days |
              <strong className="ml-2">Trucks Analyzed:</strong> {bottleneckData.total_trucks_analyzed} |
              <strong className="ml-2">Bottleneck Threshold:</strong> {bottleneckData.bottleneck_threshold_hours}h
            </p>
          </div>

          {/* Stage Timeline */}
          <div className="space-y-4 mb-6">
            {bottleneckData.stage_analytics.map((stage, index) => (
              <div key={stage.stage} className={`relative border-2 rounded-lg p-4 ${stage.is_bottleneck ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white'}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-3">
                    {stage.is_bottleneck && <AlertTriangle className="w-5 h-5 text-red-600 animate-pulse" />}
                    <span className={`px-3 py-1 rounded text-sm font-medium ${getStageColor(stage.stage)}`}>
                      {getStageLabel(stage.stage)}
                    </span>
                    <span className="text-sm text-gray-600">{stage.sample_count} samples</span>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-gray-900">{stage.avg_time_hours}h</p>
                    <p className="text-xs text-gray-500">Avg Time</p>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="relative w-full bg-gray-200 rounded-full h-4 mb-2">
                  <div
                    className={`h-4 rounded-full ${stage.is_bottleneck ? 'bg-red-500' : 'bg-blue-500'}`}
                    style={{ width: `${Math.min((stage.avg_time_hours / bottleneckData.bottleneck_threshold_hours) * 100, 100)}%` }}
                  />
                </div>

                <div className="flex justify-between text-xs text-gray-600">
                  <span>Min: {stage.min_time_hours}h</span>
                  <span>Max: {stage.max_time_hours}h</span>
                </div>
              </div>
            ))}
          </div>

          {/* Recommendations */}
          {bottleneckData.recommendations && bottleneckData.recommendations.length > 0 && (
            <div className="border-t pt-4">
              <h3 className="text-lg font-semibold mb-3 flex items-center">
                <TrendingUp className="w-5 h-5 mr-2 text-green-600" />
                Actionable Recommendations
              </h3>
              <div className="space-y-4">
                {bottleneckData.recommendations.map((rec, index) => (
                  <div key={index} className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                    <div className="flex items-start">
                      <AlertTriangle className="w-5 h-5 text-orange-600 mr-2 mt-0.5" />
                      <div className="flex-1">
                        <p className="font-semibold text-orange-800 mb-1">{rec.issue}</p>
                        <p className="text-sm text-orange-700 mb-2">Stage: {getStageLabel(rec.stage)}</p>
                        <ul className="space-y-1">
                          {rec.suggestions.map((suggestion, idx) => (
                            <li key={idx} className="text-sm text-gray-700 flex items-start">
                              <span className="text-green-600 mr-2">✓</span>
                              <span>{suggestion}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : null}

      {/* Waiting Time Analysis */}
      {waitingTimeLoading ? (
        <LoadingState message="Analyzing waiting times..." />
      ) : waitingTimeData ? (
        <div className="card">
          <div className="flex items-center space-x-2 mb-4">
            <Clock className="w-6 h-6 text-blue-600" />
            <h2 className="text-2xl font-bold">Waiting Time Analysis</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* By Material Type */}
            <div className="border rounded-lg p-4 bg-gradient-to-br from-blue-50 to-white">
              <h3 className="font-semibold mb-4 text-gray-700 flex items-center">
                <Package className="w-5 h-5 mr-2 text-blue-600" />
                By Material Type
              </h3>
              <div className="space-y-3">
                {Object.entries(waitingTimeData.by_material_type || {})
                  .sort(([, a], [, b]) => b.avg - a.avg)
                  .map(([material, stats]) => {
                    const maxAvg = Math.max(...Object.values(waitingTimeData.by_material_type || {}).map(s => s.avg));
                    const percentage = (stats.avg / maxAvg) * 100;
                    const isHigh = stats.avg >= maxAvg * 0.8;
                    return (
                      <div key={material} className="space-y-1">
                        <div className="flex items-center justify-between">
                          <span className={`text-sm font-medium ${isHigh ? 'text-red-700' : 'text-gray-700'}`}>
                            {material}
                          </span>
                          <div className="text-right">
                            <p className={`text-sm font-bold ${isHigh ? 'text-red-600' : 'text-blue-600'}`}>
                              {stats.avg}h
                            </p>
                            <p className="text-xs text-gray-500">{stats.count} trucks</p>
                          </div>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full ${isHigh ? 'bg-red-500' : 'bg-blue-500'}`}
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>

            {/* By Dock Type */}
            <div className="border rounded-lg p-4 bg-gradient-to-br from-green-50 to-white">
              <h3 className="font-semibold mb-4 text-gray-700 flex items-center">
                <Warehouse className="w-5 h-5 mr-2 text-green-600" />
                By Dock Type
              </h3>
              <div className="space-y-3">
                {Object.entries(waitingTimeData.by_dock_type || {})
                  .sort(([, a], [, b]) => b.avg - a.avg)
                  .map(([dock, stats]) => {
                    const maxAvg = Math.max(...Object.values(waitingTimeData.by_dock_type || {}).map(s => s.avg));
                    const percentage = (stats.avg / maxAvg) * 100;
                    const isHigh = stats.avg >= maxAvg * 0.8;
                    return (
                      <div key={dock} className="space-y-1">
                        <div className="flex items-center justify-between">
                          <span className={`text-sm font-medium ${isHigh ? 'text-red-700' : 'text-gray-700'}`}>
                            {dock}
                          </span>
                          <div className="text-right">
                            <p className={`text-sm font-bold ${isHigh ? 'text-red-600' : 'text-green-600'}`}>
                              {stats.avg}h
                            </p>
                            <p className="text-xs text-gray-500">{stats.count} trucks</p>
                          </div>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full ${isHigh ? 'bg-red-500' : 'bg-green-500'}`}
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>

            {/* By Time of Day */}
            <div className="border rounded-lg p-4">
              <h3 className="font-semibold mb-3 text-gray-700">By Time of Day</h3>
              <div className="space-y-2">
                {Object.entries(waitingTimeData.by_time_of_day || {}).map(([time, stats]) => (
                  <div key={time} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <span className="text-sm font-medium">{time}</span>
                    <div className="text-right">
                      <p className="text-sm font-bold">{stats.avg}h avg</p>
                      <p className="text-xs text-gray-500">{stats.count} trucks</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Top Suppliers by Wait Time */}
            <div className="border rounded-lg p-4">
              <h3 className="font-semibold mb-3 text-gray-700">Top Suppliers (by wait time)</h3>
              <div className="space-y-2">
                {Object.entries(waitingTimeData.by_supplier || {}).slice(0, 5).map(([supplier, stats]) => (
                  <div key={supplier} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <span className="text-sm font-medium truncate max-w-[180px]">{supplier}</span>
                    <div className="text-right">
                      <p className="text-sm font-bold">{stats.avg}h avg</p>
                      <p className="text-xs text-gray-500">{stats.count} trucks</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* Throughput Analysis */}
      {throughputLoading ? (
        <LoadingState message="Analyzing throughput..." />
      ) : throughputData ? (
        <div className="card">
          <div className="flex items-center space-x-2 mb-4">
            <Activity className="w-6 h-6 text-purple-600" />
            <h2 className="text-2xl font-bold">Throughput Analysis</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-600 font-medium">Avg Trucks/Day</p>
              <p className="text-3xl font-bold text-blue-700 mt-1">{throughputData.avg_trucks_per_day}</p>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-sm text-green-600 font-medium">Peak Day</p>
              <p className="text-xl font-bold text-green-700 mt-1">{throughputData.peak_day.date}</p>
              <p className="text-sm text-gray-600">{throughputData.peak_day.count} trucks</p>
            </div>
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <p className="text-sm text-purple-600 font-medium">Peak Hour</p>
              <p className="text-xl font-bold text-purple-700 mt-1">{throughputData.peak_hour.hour}</p>
              <p className="text-sm text-gray-600">{throughputData.peak_hour.count} trucks</p>
            </div>
          </div>

          {/* Hourly Distribution - Enhanced Chart */}
          <div className="border-t pt-4">
            <h3 className="font-semibold mb-4 flex items-center">
              <BarChart3 className="w-5 h-5 mr-2 text-blue-600" />
              Hourly Truck Flow Analysis
            </h3>
            <div className="bg-gray-50 rounded-lg p-6">
              {(() => {
                // Prepare data for Recharts
                const hourlyData = Object.entries(throughputData.hourly_distribution || {})
                  .sort(([a], [b]) => {
                    const hourA = parseInt(a.replace(/[^0-9]/g, ''));
                    const hourB = parseInt(b.replace(/[^0-9]/g, ''));
                    return hourA - hourB;
                  })
                  .map(([hour, count]) => ({
                    hour,
                    count,
                    trucks: count
                  }));

                const maxCount = Math.max(...hourlyData.map(d => d.count));

                // Custom bar color function
                const getBarColor = (count) => {
                  const isPeak = count === maxCount;
                  const isHigh = count >= maxCount * 0.7;
                  const isMedium = count >= maxCount * 0.4;

                  if (isPeak) return '#EF4444'; // red-500
                  if (isHigh) return '#F97316'; // orange-500
                  if (isMedium) return '#EAB308'; // yellow-500
                  return '#60A5FA'; // blue-400
                };

                // Custom tooltip
                const CustomTooltip = ({ active, payload }) => {
                  if (active && payload && payload.length) {
                    const data = payload[0].payload;
                    const isPeak = data.count === maxCount;

                    return (
                      <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3">
                        <p className="font-semibold text-gray-900 mb-1">{data.hour}</p>
                        <p className="text-sm text-gray-600">
                          <span className="font-medium text-blue-600">{data.trucks}</span> trucks
                        </p>
                        {isPeak && (
                          <div className="flex items-center mt-1 text-xs text-red-600">
                            <TrendingUp className="w-3 h-3 mr-1" />
                            Peak Hour
                          </div>
                        )}
                      </div>
                    );
                  }
                  return null;
                };

                return (
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={hourlyData}
                        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                        <XAxis
                          dataKey="hour"
                          tick={{ fontSize: 12, fill: '#6B7280' }}
                          axisLine={{ stroke: '#D1D5DB' }}
                          tickLine={{ stroke: '#D1D5DB' }}
                        />
                        <YAxis
                          tick={{ fontSize: 12, fill: '#6B7280' }}
                          axisLine={{ stroke: '#D1D5DB' }}
                          tickLine={{ stroke: '#D1D5DB' }}
                          label={{ value: 'Number of Trucks', angle: -90, position: 'insideLeft', style: { fontSize: 12, fill: '#6B7280' } }}
                        />
                        <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} />
                        <Bar
                          dataKey="trucks"
                          radius={[8, 8, 0, 0]}
                          maxBarSize={60}
                        >
                          {hourlyData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={getBarColor(entry.count)} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                );
              })()}

              {/* Legend */}
              <div className="flex items-center justify-center space-x-6 mt-6 pt-4 border-t">
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 bg-red-500 rounded"></div>
                  <span className="text-xs text-gray-600 font-medium">Peak Hour</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 bg-orange-500 rounded"></div>
                  <span className="text-xs text-gray-600">High (70%+)</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 bg-yellow-500 rounded"></div>
                  <span className="text-xs text-gray-600">Medium (40-70%)</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 bg-blue-400 rounded"></div>
                  <span className="text-xs text-gray-600">Low (&lt;40%)</span>
                </div>
              </div>
            </div>

            {/* Peak Time Insights */}
            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="flex items-center space-x-2">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                  <p className="font-semibold text-red-800">Peak Time Alert</p>
                </div>
                <p className="text-sm text-gray-700 mt-2">
                  Highest traffic at <span className="font-bold">{throughputData.peak_hour.hour}</span> with <span className="font-bold">{throughputData.peak_hour.count} trucks</span>
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-center space-x-2">
                  <Clock className="w-5 h-5 text-blue-600" />
                  <p className="font-semibold text-blue-800">Optimal Scheduling</p>
                </div>
                <p className="text-sm text-gray-700 mt-2">
                  Spread arrivals across off-peak hours to reduce wait times
                </p>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <p className="font-semibold text-green-800">Resource Planning</p>
                </div>
                <p className="text-sm text-gray-700 mt-2">
                  Allocate more staff during <span className="font-bold text-orange-600">orange</span> and <span className="font-bold text-red-600">red</span> hours
                </p>
              </div>
            </div>
          </div>

          {/* Status Distribution */}
          {throughputData.status_distribution && (
            <div className="border-t pt-4 mt-4">
              <h3 className="font-semibold mb-3">Status Distribution</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {throughputData.status_distribution.map((item) => (
                  <div key={item.status} className="bg-gray-50 border rounded-lg p-3 text-center">
                    <p className="text-sm text-gray-600">{item.status}</p>
                    <p className="text-2xl font-bold text-gray-900">{item.count}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default OperationalAnalytics;
